// src/context/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase";
import {
  User,
  onAuthStateChanged,
} from "firebase/auth";
import api from "../api/client";
import type { AccountStatus, OnboardingStatus } from "../types/auth";
import { trackAuthComplete } from "../lib/analytics";

export type AuthSessionIssue = "account_exists_for_email";

interface AuthContextType {
  currentUser: User | null;
  businessId: string | null;
  accountStatus: AccountStatus | null;
  sessionIssue: AuthSessionIssue | null;
  sessionIssueMessage: string | null;
  onboardingStatus: Partial<OnboardingStatus> | null;
  onboardingCompleted: boolean;
  getIdToken: () => Promise<string | null>;
  setBusinessId: (id: string | null) => void;
  initializing: boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  const [sessionIssue, setSessionIssue] = useState<AuthSessionIssue | null>(null);
  const [sessionIssueMessage, setSessionIssueMessage] = useState<string | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<Partial<OnboardingStatus> | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const getIdToken = async () => {
    if (!currentUser) return null;
    try {
      return await currentUser.getIdToken();
    } catch (err) {
      console.error("❌ Error getting Firebase ID token:", err);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (!user) {
        setBusinessId(null);
        setAccountStatus(null);
        setSessionIssue(null);
        setSessionIssueMessage(null);
        setOnboardingStatus(null);
        setOnboardingCompleted(false);
        setInitializing(false);
        return;
      }

      try {
        const authMethod = user.providerData.find((provider) => provider.providerId)?.providerId ?? "firebase";
        trackAuthComplete(authMethod, window.location.pathname);
        const response = await api.get("/isRegistered");
        const data = response?.data;

        if (data?.registered) {
          setBusinessId(data.business_id);
          setAccountStatus(data.account_status ?? null);
          setSessionIssue(null);
          setSessionIssueMessage(null);
          setOnboardingStatus(data.onboarding_status ?? null);
          setOnboardingCompleted(!!data.onboarding_completed_at);

          if (data.account_status === "pending_verification" && user.emailVerified) {
            try {
              await api.post("/verifyEmailComplete", { email_verified: true });
              setAccountStatus("trial");
            } catch {
              console.warn("Unable to complete email verification status update");
            }
          }
        } else {
          setBusinessId(null);
          setAccountStatus(null);
          setSessionIssue(data?.reason ?? null);
          setSessionIssueMessage(data?.message ?? null);
          setOnboardingStatus(null);
          setOnboardingCompleted(false);
        }
      } catch (err) {
        console.error("🚨 Error fetching registration status:", err);
        setBusinessId(null);
        setAccountStatus(null);
        setSessionIssue(null);
        setSessionIssueMessage(null);
        setOnboardingStatus(null);
        setOnboardingCompleted(false);
      } finally {
        setInitializing(false);
      }
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        businessId,
        accountStatus,
        sessionIssue,
        sessionIssueMessage,
        onboardingStatus,
        onboardingCompleted,
        getIdToken,
        setBusinessId,
        initializing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
